import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Drawer, SageClassnames, Frame, Modal, Button, Icon } from '@kajabi/sage-react';
import { Formik, Form } from 'formik';

import { useUiResourceContext } from 'common/components/CopyMagic/contexts';
import useSubmitPromptSetNewMessages from 'common/components/CopyMagic/hooks/useSubmitPromptSetNewMessages';
import ErrorMessage from 'common/components/CopyMagic/components/ErrorMessage';
import SupportButton from 'common/components/CopyMagic/components/SupportButton';
import { chatSchema } from 'common/components/CopyMagic/shared/validationSchema';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import { useMessagesContext } from 'common/components/CopyMagic/contexts/MessagesContext';
import { CHAT_EVENT_TYPES } from 'common/components/CopyMagic/constants';
import useChatSessionId from 'common/components/CopyMagic/hooks/useChatSessionId';
import postChatEvent from 'common/components/CopyMagic/api/postChatEvent';

import useChatSession from '../../hooks/useChatSession';
import MessagesDisplay from '../MessagesDisplay';
import InputForm from '../InputForm';
import FullscreenButton from '../FullscreenButton';

import amaLogo from '../../images/amaLogoV2.png';

const DRAWER_SCROLLABLE_SELECTOR =
  '#copy-magic-conversation-assistant.sage-drawer > .sage-modal__container > .sage-modal__content';

const ConversationAssistantDrawerV2 = () => {
  const { open, setOpen, error } = useUiResourceContext();
  const { initialValues, submitPrompt } = useSubmitPromptSetNewMessages();
  const { messages } = useMessagesContext();
  const trackCloseClicked = useGeneratorTrackingCallback('useTrackCloseClicked');
  const { endChatSession, isChatSessionInProgress } = useChatSession();
  const { getChatSessionIdWithStatus } = useChatSessionId();

  const handleExit = () => {
    setOpen(false);
    trackCloseClicked();
    endChatSession();
  };
  // Scroll to end when message length changes or an error occurs
  useEffect(() => {
    const scrollElement = window.document.querySelector(DRAWER_SCROLLABLE_SELECTOR);
    if (!scrollElement) return;
    scrollElement.scrollTop = scrollElement.scrollHeight;
  }, [messages.size, error]);

  useEffect(() => {
    if (!open && isChatSessionInProgress()) {
      setOpen(true);
    }
  }, [open, setOpen, isChatSessionInProgress]);

  useEffect(() => {
    if (!open) return;
    const { chatSessionId, isNew } = getChatSessionIdWithStatus();

    if (isNew) {
      // Track session started when the drawer is opened and a new session is created
      postChatEvent({
        sessionId: chatSessionId,
        eventType: CHAT_EVENT_TYPES.STARTED,
      });
    }
  }, [open, getChatSessionIdWithStatus]);

  const drawerHeader = (
    <Frame
      align={Frame.ALIGNMENTS.CENTER_SPREAD}
      direction={Frame.DIRECTIONS.HORIZONTAL}
      width={Frame.WIDTHS.FILL}
    >
      <Frame
        align={Frame.ALIGNMENTS.CENTER_LEFT}
        direction={Frame.DIRECTIONS.HORIZONTAL}
        gap={Frame.GAPS.SM}
      >
        <img alt="Ama" src={amaLogo} arid-hidden="true" width="32px" />
        <h2 className={SageClassnames.TYPE.HEADING_5}>Ama</h2>
      </Frame>
      <Frame
        align={Frame.ALIGNMENTS.CENTER_LEFT}
        direction={Frame.DIRECTIONS.HORIZONTAL}
        gap={Frame.GAPS.XS}
        width={Frame.WIDTHS.FLEX}
      >
        <FullscreenButton />
        <SupportButton />
        <Button
          color={Button.COLORS.SECONDARY}
          icon={Icon.ICONS.REMOVE}
          iconOnly={true}
          subtle={true}
          onClick={handleExit}
        >
          Close Ama
        </Button>
      </Frame>
    </Frame>
  );

  const drawerFooter = (
    <Formik
      initialValues={initialValues}
      onSubmit={submitPrompt}
      validationSchema={chatSchema}
      validateOnMount
      enableReinitialize
    >
      <Form className="copy-magic-conversation-assistant__form">
        <InputForm />
      </Form>
    </Formik>
  );

  return ReactDOM.createPortal(
    <div>
      <Drawer
        active={open}
        compact
        customWidth={500}
        showClose={false}
        disableBackgroundDismiss={true}
        onExit={handleExit}
        footer={drawerFooter}
        id="copy-magic-conversation-assistant"
        customHeader={drawerHeader}
        className="copy-magic-conversation-assistant copy-magic-conversation-assistant--v2"
      >
        <MessagesDisplay />
        <ErrorMessage />
      </Drawer>
      <Modal />
    </div>,
    document.body,
  );
};

export default ConversationAssistantDrawerV2;
